import { useState } from 'react';
import { cn } from '../../styles/lib/utils';
import type { FilterForm, FilterItem, FilterType } from '../../utils/filterSettings';
import { Button } from '../Button';
import { Icon } from '../Icon';
import { Text } from '../Text';
import { AdvancedFilterActionMenu, type AdvancedFilterActionMenuAction } from './AdvancedFilterActionMenu';

import { AdvancedFilterLine } from './AdvancedFilterLine';
import { AdvancedFilterOperatorSelector, AdvancedFilterOperatorText } from './components';
import { getFilterPosition, getGroupOperatorPath } from './utils';

export type AdvancedFilterGroupProps = {
  form: FilterForm;
  fieldPath: string;
  itemType: 'line' | 'group';
  filterTypes?: FilterType[];
  className?: string;
  shouldShowOperatorSelector?: boolean;
  shouldShowOperatorText?: boolean;
  depth?: number;
  onMakeLineGroup: (fieldPath: string) => void;
  onMakeGroupGroup: (fieldPath: string) => void;
  onDuplicate: (fieldPath: string) => void;
  onDelete: (fieldPath: string) => void;
  onAddFilter: (fieldPath: string) => void;
};

function AdvancedFilterGroup({
  form,
  fieldPath,
  itemType,
  filterTypes = [],
  className,
  shouldShowOperatorSelector = false,
  shouldShowOperatorText = false,
  depth = 0,
  onMakeLineGroup,
  onMakeGroupGroup,
  onDuplicate,
  onDelete,
  onAddFilter,
}: AdvancedFilterGroupProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Handle both line and group items
  if (itemType === 'line') {
    return (
      <AdvancedFilterLine
        form={form}
        fieldPath={fieldPath}
        filterTypes={filterTypes}
        shouldShowOperatorSelector={shouldShowOperatorSelector}
        shouldShowOperatorText={shouldShowOperatorText}
        onMakeLineGroup={onMakeLineGroup}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        parentDepth={depth}
      />
    );
  }

  // Calculate background color based on depth (alternate between grey-10 and grey-20)
  const bgColor = depth % 2 === 0 ? 'bg-grey-10' : 'bg-grey-20';

  const menuActions: AdvancedFilterActionMenuAction[] = [
    { label: 'Make filter group', icon: 'plus', onClick: () => onMakeGroupGroup(fieldPath) },
    { label: 'Duplicate', icon: 'copy', onClick: () => onDuplicate(fieldPath) },
    { label: 'Delete', icon: 'trash', onClick: () => onDelete(fieldPath) },
  ];

  const operatorPath = getGroupOperatorPath(fieldPath);
  const groupName = `Filter group ${getFilterPosition(fieldPath)}`;

  return (
    <div className={cn('flex gap-1 p-1 w-fit', isMenuOpen && 'rounded-sm bg-blue-10', className)}>
      <div className="flex gap-2 items-center relative">
        {/* Operator selector - shown on first item when group has multiple items */}
        {shouldShowOperatorSelector && <AdvancedFilterOperatorSelector form={form} operatorPath={operatorPath} />}
        {/* Operator text - shown on non-first items in a group */}
        {shouldShowOperatorText && <AdvancedFilterOperatorText form={form} operatorPath={operatorPath} />}

        {/* Group container */}
        <div className={cn(bgColor, 'border border-grey-30 rounded flex flex-col gap-2 items-start p-2')}>
          <form.Field name={`${fieldPath}.items`}>
            {(itemsField: any) => {
              const items = itemsField.state.value || [];
              if (items.length === 0) {
                return null;
              }
              const hasMultipleItems = items.length > 1;
              return (
                <div className={cn(bgColor, 'flex flex-col')}>
                  {items.map((item: FilterItem, index: number) => {
                    const isFirstItem = index === 0;
                    // When there are multiple items, first item gets the selector, others get renderOperator
                    // When there's only one item, no operator is shown
                    const shouldShowOperatorSelector = hasMultipleItems && isFirstItem;
                    const shouldShowOperatorText = hasMultipleItems && !isFirstItem;
                    const nestedFieldPath = `${fieldPath}.items[${index}]`;

                    return (
                      <form.Field key={item.id} name={nestedFieldPath}>
                        {(itemField: any) => (
                          <AdvancedFilterGroup
                            form={form}
                            fieldPath={nestedFieldPath}
                            itemType={itemField.state?.value?.type || 'line'}
                            filterTypes={filterTypes}
                            shouldShowOperatorSelector={shouldShowOperatorSelector}
                            shouldShowOperatorText={shouldShowOperatorText}
                            depth={depth + 1}
                            onMakeLineGroup={onMakeLineGroup}
                            onMakeGroupGroup={onMakeGroupGroup}
                            onDuplicate={onDuplicate}
                            onDelete={onDelete}
                            onAddFilter={onAddFilter}
                          />
                        )}
                      </form.Field>
                    );
                  })}
                </div>
              );
            }}
          </form.Field>

          <Button
            type="button"
            variant="hollow"
            size="small"
            className="gap-1 text-xs font-medium text-blue-50"
            onClick={() => onAddFilter(fieldPath)}
            aria-label={`Add Filter to ${groupName.toLowerCase()}`}
          >
            <Icon icon="plus" size={14} />
            <Text className="text-xs text-blue-50">Add Filter</Text>
          </Button>
        </div>

        {/* Vertical dots menu */}
        {menuActions.length > 0 && (
          <AdvancedFilterActionMenu
            actions={menuActions}
            onOpenChange={setIsMenuOpen}
            depth={depth}
            aria-label={`${groupName} actions`}
          />
        )}
      </div>
    </div>
  );
}

export { AdvancedFilterGroup };
